import { c as _c } from "react/compiler-runtime";
import { Badge, Box, capitalize, Stack, Tooltip, Typography, type BoxProps } from '@mui/material';
import { type ApiCredentialsProperties, type User } from '@repo/api-gw-sdk';
import { forwardRef } from 'react';
import { Icon } from '@/components/shared/icon';
import { useUser } from '@/contexts/useUser';
import { ApprovalDetailsTooltip } from '../mpa/approvalDetailsTooltip';
import { useMpaRequestsCounts } from '../mpa/useMpaRequestsCounts';
export const getUserName = (user?: User | ApiCredentialsProperties, ignoreDomain?: boolean) => {
  let name = '';
  if (user) {
    if ('givenName' in user && user.givenName) {
      name = capitalize(user.givenName);
    }
    if (!name && 'name' in user && user.name) {
      name = capitalize(user.name);
    }
    if (!name && 'email' in user && user.email) {
      name = user.email;
    }
  }
  return ignoreDomain ? name?.split('@')[0] : name;
};
export const UserCircleWithNotificationBadge = forwardRef((props, ref) => {
  const $ = _c(11);
  const {
    requests,
    myRequests,
    shouldShowCounts
  } = useMpaRequestsCounts();
  const awaitingApprovalCount = requests?.totalCount ?? 0;
  const readyToApplyCount = myRequests?.totalCount ?? 0;
  const requestCount = awaitingApprovalCount + readyToApplyCount;
  if (!requestCount || !shouldShowCounts) {
    let t0;
    if ($[0] !== props || $[1] !== ref) {
      t0 = <UserCircle {...props} ref={ref} />;
      $[0] = props;
      $[1] = ref;
      $[2] = t0;
    } else {
      t0 = $[2];
    }
    return t0;
  }
  let t0;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      "& .MuiBadge-badge": {
        animation: "breathingPulse 2.5s ease-in-out infinite",
        "@keyframes breathingPulse": {
          "0%, 100%": {
            transform: "scale(1) translate(50%, -50%)",
            boxShadow: "0 0 0 0 var(--mui-palette-primary-main)"
          },
          "50%": {
            transform: "scale(1.5) translate(50%, -50%)",
            boxShadow: "0 0 0 2px color-mix(in srgb, var(--mui-palette-primary-main) 30%, transparent)"
          }
        }
      }
    };
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  let t1;
  if ($[4] !== awaitingApprovalCount || $[5] !== readyToApplyCount) {
    t1 = <ApprovalDetailsTooltip awaitingApprovalCount={awaitingApprovalCount} readyToApplyCount={readyToApplyCount} />;
    $[4] = awaitingApprovalCount;
    $[5] = readyToApplyCount;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== props || $[8] !== ref || $[9] !== t1) {
    t2 = <Badge badgeContent=" " overlap="circular" color="primary" variant="dot" sx={t0}><UserCircle {...props} ref={ref} tooltip={t1} /></Badge>;
    $[7] = props;
    $[8] = ref;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
});
interface UserCircleProps extends BoxProps {
  user?: User | ApiCredentialsProperties;
  ignoreDomain?: boolean;
  text?: string;
  tooltip?: React.ReactNode;
}
export const UserCircle = forwardRef((t0, ref) => {
  const $ = _c(20);
  let ignoreDomain;
  let rest;
  let text;
  let tooltip;
  let user;
  if ($[0] !== t0) {
    ({
      user,
      ignoreDomain,
      text,
      tooltip,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = ignoreDomain;
    $[2] = rest;
    $[3] = text;
    $[4] = tooltip;
    $[5] = user;
  } else {
    ignoreDomain = $[1];
    rest = $[2];
    text = $[3];
    tooltip = $[4];
    user = $[5];
  }
  let t1;
  if ($[6] !== ignoreDomain || $[7] !== text || $[8] !== user) {
    t1 = text || getUserName(user, ignoreDomain);
    $[6] = ignoreDomain;
    $[7] = text;
    $[8] = user;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  const name = t1;
  let t2;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      tooltip: {
        sx: {
          maxWidth: "none",
          p: "0px",
          bgcolor: "transparent",
          boxShadow: "none"
        }
      }
    };
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const t3 = name || "";
  let t4;
  if ($[11] !== t3[0]) {
    t4 = <Typography className="font-semibold text-white capitalize">{t3[0]}</Typography>;
    $[11] = t3[0];
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== ref || $[14] !== rest || $[15] !== t4) {
    t5 = <Box {...rest} ref={ref} className="min-w-[24px] min-h-[24px] w-[24px] h-[24px] flex items-center justify-center rounded-full bg-(--mui-palette-success-main)">{t4}</Box>;
    $[13] = ref;
    $[14] = rest;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== t5 || $[18] !== tooltip) {
    t6 = <Tooltip disableInteractive={false} slotProps={t2} title={tooltip}>{t5}</Tooltip>;
    $[17] = t5;
    $[18] = tooltip;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  return t6;
});
export const UserBadge = t0 => {
  const $ = _c(15);
  const {
    user,
    className
  } = t0;
  const {
    user: currentUser
  } = useUser();
  const t1 = user.givenName || user.email || "";
  let t2;
  if ($[0] !== t1) {
    t2 = t1.split("@");
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const name = t2[0];
  const isSamlUser = user.userOrigin && user.userOrigin !== "LOCAL_USER";
  const nameSuffix = currentUser?.id === user.id ? " (you)" : isSamlUser ? " (SAML)" : "";
  let t3;
  if ($[2] !== user) {
    t3 = <UserCircle user={user} ignoreDomain={true} />;
    $[2] = user;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = name + nameSuffix;
  let t5;
  if ($[4] !== t4) {
    t5 = <Typography variant="subtitle1">{t4}</Typography>;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== user.email) {
    t6 = <Typography variant="body1">{user.email}</Typography>;
    $[6] = user.email;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t5 || $[9] !== t6) {
    t7 = <Stack className="ml-[12px]">{t5}{t6}</Stack>;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== className || $[12] !== t3 || $[13] !== t7) {
    t8 = <Stack direction="row" alignItems="center" className={className}>{t3}{t7}</Stack>;
    $[11] = className;
    $[12] = t3;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  return t8;
};
export const EntityBadge = t0 => {
  const $ = _c(13);
  const {
    title,
    subtitle,
    className
  } = t0;
  const t1 = title || subtitle;
  let t2;
  if ($[0] !== t1) {
    t2 = <UserCircle text={t1} />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== title) {
    t3 = <Typography variant="subtitle1">{title}</Typography>;
    $[2] = title;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== subtitle) {
    t4 = <Typography variant="body1">{subtitle}</Typography>;
    $[4] = subtitle;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t3 || $[7] !== t4) {
    t5 = <Stack className="ml-[12px]">{t3}{t4}</Stack>;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== className || $[10] !== t2 || $[11] !== t5) {
    t6 = <Stack direction="row" alignItems="center" className={className}>{t2}{t5}</Stack>;
    $[9] = className;
    $[10] = t2;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
};
export const EntityBadgeWithIcon = t0 => {
  const $ = _c(13);
  const {
    title,
    subtitle,
    icon,
    className
  } = t0;
  let t1;
  if ($[0] !== icon) {
    t1 = <Icon iconClass={icon} />;
    $[0] = icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== title) {
    t2 = <Typography variant="subtitle1">{title}</Typography>;
    $[2] = title;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== subtitle) {
    t3 = <Typography variant="body1">{subtitle}</Typography>;
    $[4] = subtitle;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <Stack className="ml-[12px]">{t2}{t3}</Stack>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== className || $[10] !== t1 || $[11] !== t4) {
    t5 = <Stack direction="row" alignItems="center" className={className}>{t1}{t4}</Stack>;
    $[9] = className;
    $[10] = t1;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
};